<template>
    <div class="roster" :style="{ '--team-color': color || '#ff7133' }">
        <div class="roster-summary">
            <div class="roster-summary__item">
                <v-icon color="orange">mdi-account-group</v-icon>
                <span class="roster-summary__value">{{ athletes.length }}</span>
                <span class="roster-summary__label">Παίκτες</span>
            </div>
            <div v-if="avgHeight" class="roster-summary__item">
                <v-icon color="orange">mdi-human-male-height</v-icon>
                <span class="roster-summary__value">{{ avgHeight }} cm</span>
                <span class="roster-summary__label">Μέσο ύψος</span>
            </div>
            <div v-if="avgAge" class="roster-summary__item">
                <v-icon color="orange">mdi-cake-variant-outline</v-icon>
                <span class="roster-summary__value">{{ avgAge }}</span>
                <span class="roster-summary__label">Μέση ηλικία</span>
            </div>
        </div>

        <section v-for="group in groups" :key="group.key" class="roster-group">
            <div class="roster-group__title">
                <span class="roster-group__bar custom-orange" />
                {{ group.title }}
                <span class="roster-group__count">{{ group.players.length }}</span>
            </div>
            <v-row>
                <v-col v-for="player in group.players" :key="player.id" cols="12" sm="6" md="4" lg="3">
                    <!-- the coach card is info only, the players open their page -->
                    <v-card
                        class="roster-card rounded-lg"
                        :class="{ 'roster-card--info': group.key === 'coach' }"
                        :hover="group.key !== 'coach'"
                        v-on="group.key === 'coach' ? {} : { click: () => goToPlayer(player) }"
                    >
                        <span v-if="player.coach && group.key === 'coach'" class="roster-card__badge">
                            <v-icon x-small color="white">mdi-whistle</v-icon>
                            Coach
                        </span>
                        <span v-else-if="player.jersey !== null && player.jersey !== undefined" class="roster-card__jersey">
                            {{ player.jersey }}
                        </span>
                        <div class="roster-card__photo">
                            <img
                                :src="player.image ? `${assetsUrl}/${player.image}` : defaultPlayerImg"
                                alt=""
                                @error="$event.target.src = defaultPlayerImg"
                            >
                        </div>
                        <div class="roster-card__info">
                            <div class="roster-card__name">{{ player.name }} {{ player.surname }}</div>
                            <div class="roster-card__facts">
                                <span v-if="player.leader" class="roster-card__leader">
                                    <v-icon x-small color="orange darken-2">mdi-star</v-icon>
                                    Αρχηγός
                                </span>
                                <span v-if="heightCm(player)">{{ heightCm(player) }} cm</span>
                                <span v-if="age(player)">{{ age(player) }} ετών</span>
                            </div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </section>

        <div v-if="!groups.length" class="roster-empty">Δεν υπάρχουν παίκτες στο ρόστερ.</div>
    </div>
</template>

<script>
    import moment from 'moment'

    const POSITION_GROUPS = [
        { key: 'guard', title: 'Guards' },
        { key: 'forward', title: 'Forwards' },
        { key: 'center', title: 'Centers' }
    ]

    export default {
        props: {
            players: {
                type: Array,
                default: null
            },
            color: {
                type: String,
                default: null
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png')
            }
        },
        computed: {
            roster() {
                return Array.isArray(this.players) ? this.players : []
            },
            athletes() {
                return this.roster.filter(this.isAthlete)
            },
            groups() {
                const byJersey = (a, b) => (Number(a.jersey) || 999) - (Number(b.jersey) || 999)
                const athletes = this.athletes
                const groups = [{
                    key: 'coach',
                    title: 'Προπονητές',
                    // a coach is shown as coach whether active or not
                    players: this.roster.filter(player => player.coach)
                }]
                POSITION_GROUPS.forEach(position => {
                    groups.push({ ...position, players: athletes.filter(player => player.position === position.key).sort(byJersey) })
                })
                // a player without a position is not shown in the roster
                return groups.filter(group => group.players.length)
            },
            avgHeight() {
                const heights = this.athletes.map(player => this.heightCm(player)).filter(Boolean)
                return heights.length ? Math.round(heights.reduce((sum, value) => sum + value, 0) / heights.length) : null
            },
            avgAge() {
                const ages = this.athletes.map(player => this.age(player)).filter(Boolean)
                return ages.length ? Math.round(ages.reduce((sum, value) => sum + value, 0) / ages.length) : null
            }
        },
        methods: {
            hasPosition(player) {
                return POSITION_GROUPS.some(position => position.key === player.position)
            },
            // Only the active ones play; an active coach with a position (player-coach) is in both the coaches and their position group
            isAthlete(player) {
                return player.active && (!player.coach || this.hasPosition(player))
            },
            // Heights are stored either in metres (1.85) or centimetres (185)
            heightCm(player) {
                const value = Number(player.height)
                if (!value) return null
                return Math.round(value < 3 ? value * 100 : value)
            },
            age(player) {
                if (!player.birthday) return null
                const birthday = moment(player.birthday, 'DD/MM/YYYY')
                return birthday.isValid() ? moment().diff(birthday, 'years') : null
            },
            goToPlayer(player) {
                this.$router.push({ name: 'Player', params: { id: player.id } })
            }
        }
    }
</script>

<style scoped>
.roster {
    font-family: Ubuntu, Arial, sans-serif;
    text-align: left;
}

.roster-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.roster-summary__item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.roster-summary__value {
    font-size: 1.2rem;
    font-weight: 800;
}

.roster-summary__label {
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.8rem;
    font-weight: 700;
}

.roster-group {
    margin-top: 20px;
}

.roster-group__title {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    background: #fff;
    margin-bottom: 14px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    font-size: 1.05rem;
    font-weight: 800;
}

.roster-group__bar {
    width: 5px;
    height: 20px;
    border-radius: 2px;
}

.roster-group__count {
    padding: 0 8px;
    border-radius: 10px;
    background: #0d0d0f;
    color: #fff;
    font-size: 0.75rem;
}

/* ---------- Card (same diagonal team-color style as the teams page) ---------- */
.roster-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    height: 100%;
    min-height: 96px;
    padding: 10px 14px;
    background: linear-gradient(110deg, var(--team-color) 24%, #fff 24%);
    overflow: hidden;
    transition: transform 0.15s ease;
}

.roster-card:not(.roster-card--info):hover {
    transform: translateY(-3px);
}

/* the coach: info only, nothing to click */
.roster-card--info {
    cursor: default;
}

.roster-card__photo {
    flex: 0 0 72px;
    width: 72px;
    height: 76px;
}

.roster-card__photo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.roster-card__info {
    flex: 1 1 auto;
    min-width: 0;
    padding-right: 34px;
}

.roster-card__name {
    font-weight: 800;
    line-height: 1.2;
    word-break: break-word;
}

.roster-card__facts {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 10px;
    margin-top: 4px;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.78rem;
    font-weight: 600;
}

.roster-card__leader {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    color: #e65100;
}

.roster-card__jersey {
    position: absolute;
    top: 8px;
    right: 10px;
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.8rem;
    font-weight: 800;
    line-height: 1;
    color: rgba(0, 0, 0, 0.18);
}

.roster-card__badge {
    position: absolute;
    top: 8px;
    right: 8px;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 1px 8px;
    border-radius: 4px;
    background: #0d0d0f;
    color: #fff;
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.roster-empty {
    margin-top: 16px;
    padding: 24px 16px;
    background: #fff;
    border-radius: 12px;
    color: rgba(0, 0, 0, 0.6);
    font-weight: 600;
    text-align: center;
}
</style>
